import { z } from "zod";
import { IdSchema, ScopeSchema, TimestampSchema, WpIdSchema } from "./primitives.ts";

/**
 * How a claim tolerates other claims on the same scope.
 * - `exclusive`: "nobody else should touch this while I hold it". Any overlap with it blocks.
 * - `shared`: "I will touch this but can coexist". Overlaps between two shared claims only warn.
 */
export const ClaimModeSchema = z.enum(["exclusive", "shared"]);
/** See {@link ClaimModeSchema}. */
export type ClaimMode = z.infer<typeof ClaimModeSchema>;

/**
 * Lifecycle of a claim.
 * - `active`: in force.
 * - `queued`: a migration claim waiting for the migration lane. Only one migration claim per repo
 *   is active at a time; the rest queue in declaration order and become `active` when it is their turn.
 * - `released`: its owner let it go, a resolution handed it over, or its session landed.
 * - `expired`: its lease ran out because the session stopped heartbeating.
 */
export const ClaimStatusSchema = z.enum(["active", "queued", "released", "expired"]);
/** See {@link ClaimStatusSchema}. */
export type ClaimStatus = z.infer<typeof ClaimStatusSchema>;

/** Claim statuses that still count for overlap detection. */
export const LIVE_CLAIM_STATUSES: readonly ClaimStatus[] = ["active", "queued"];

/**
 * A declared intent to edit something, made before writing code.
 * Claims are leases: each heartbeat from the owning session pushes `expiresAt` forward,
 * so a dead agent's claims lapse on their own.
 *
 * Tip: put migration work in its own claim, so that if the migration lane is busy only that claim queues.
 */
export const IntentClaimSchema = z.object({
  /** Coordinator-assigned id. */
  id: IdSchema,
  /** The session that holds the claim. */
  sessionId: IdSchema,
  /** The work package the claim serves (always the session's work package). */
  wpId: WpIdSchema,
  /** What the claim covers: paths, symbols, contracts and the migration flag. */
  scope: ScopeSchema,
  /** Why the agent needs this scope, in a sentence. Shown to any agent it collides with. */
  rationale: z.string().min(1).max(2000),
  /** Exclusive or shared, see {@link ClaimModeSchema}. */
  mode: ClaimModeSchema,
  /** Current lifecycle state, see {@link ClaimStatusSchema}. */
  status: ClaimStatusSchema,
  /** When the claim was declared. */
  createdAt: TimestampSchema,
  /** When the lease lapses unless renewed by a heartbeat. */
  expiresAt: TimestampSchema,
});
/** See {@link IntentClaimSchema}. */
export type IntentClaim = z.infer<typeof IntentClaimSchema>;
